import { motion as Motion } from "framer-motion";
import AnswerStageCard from "./AnswerStageCard";
import GuessStageCard from "./GuessStageCard";
import ResultStageCard from "./ResultStageCard";
import PenaltyChoiceCard from "./PenaltyChoiceCard";
import DareStageCard from "./DareStageCard";
import cls from "./GameTurnStage.module.scss";
import { resolveQuestionBackgroundUrl } from "./questionMedia";

export default function GameTurnStage({
  currentTurnView,
  penaltyView,
  onAnswer,
  onGuess,
  onChoosePenaltyMode,
  onConfirmCoinPaid,
  onConfirmDareDone,
  loading,
  recoveryStage = "",
}) {
  const safeView = currentTurnView || {};
  const turn = safeView?.turn || null;
  const status =
    turn?.status || currentTurnView?.liveState?.turnPhase || "idle";
  const role =
    status === "penalty_pending" || status === "dare_active"
      ? penaltyView?.role || currentTurnView?.role || "viewer"
      : currentTurnView?.role || penaltyView?.role || "viewer";

  if (!turn) {
    return (
      <section className={cls.stageShell}>
        <div className={cls.glowPink} />
        <div className={cls.glowBlue} />
        <div className={cls.gridLines} />

        <div className={cls.stageHeader}>
          <p className={cls.kicker}>TURN STAGE</p>
          <h3 className={cls.title}>Waiting for next turn</h3>
          {recoveryStage === "waiting_recovery" ? (
            <p className={cls.subtitle}>
              Game is syncing a previous turn. Please stay on this screen.
            </p>
          ) : null}
        </div>
      </section>
    );
  }

  if (status === "result_ready") {
    return (
      <ResultStageCard
        turn={turn}
        players={currentTurnView?.players}
        penaltyView={penaltyView}
      />
    );
  }

  if (status === "penalty_pending" || turn?.penaltyState === "choice_pending") {
    return (
      <PenaltyChoiceCard
        turn={turn}
        penaltyView={penaltyView}
        loading={loading}
        onChooseMode={onChoosePenaltyMode}
        onConfirmCoinPaid={onConfirmCoinPaid}
      />
    );
  }

  if (status === "dare_active") {
    return (
      <DareStageCard
        turn={turn}
        penaltyView={penaltyView}
        loading={loading}
        onConfirmDareDone={onConfirmDareDone}
      />
    );
  }

  if (role === "answer" && status === "answer_pending") {
    return (
      <AnswerStageCard
        turn={turn}
        players={currentTurnView?.players}
        loading={loading}
        onSubmit={onAnswer}
      />
    );
  }

  if (role === "guess" && status === "guess_pending") {
    return (
      <GuessStageCard
        turn={turn}
        players={currentTurnView?.players}
        loading={loading}
        onSubmit={onGuess}
      />
    );
  }

  const question = turn?.questionSnapshot || {};
  const answerPlayer = currentTurnView?.players?.answerPlayer;
  const guessPlayer = currentTurnView?.players?.guessPlayer;
  const backgroundUrl = resolveQuestionBackgroundUrl(question);

  if (status === "answer_pending" || status === "guess_pending") {
    const waitingTitle =
      role === "guess" && status === "answer_pending"
        ? `You are playing with ${answerPlayer?.displayName || "your partner"}`
        : role === "answer" && status === "guess_pending"
          ? `You are playing with ${guessPlayer?.displayName || "your partner"}`
          : `${answerPlayer?.displayName || "your partner"} & ${guessPlayer?.displayName || "your partner"} are playing`;

    const waitingText =
      role === "guess" && status === "answer_pending"
        ? `Please wait until ${answerPlayer?.displayName || "your partner"} answers the question.`
        : role === "answer" && status === "guess_pending"
          ? `You already answered. Wait while ${guessPlayer?.displayName || "your partner"} submits the guess.`
          : status === "answer_pending"
            ? `${answerPlayer?.displayName || "your partner"} still thinking ...`
            : `now, ${guessPlayer?.displayName || "your partner"} is guessing ...`;

    return (
      <section className={cls.stageShell}>
        <div className={cls.glowPink} />
        <div className={cls.glowBlue} />
        <div className={cls.gridLines} />

        <div className={cls.stageHeader}>
          <p className={cls.kicker}>TURN STAGE</p>
          <h3 className={cls.title}>{waitingTitle}</h3>
          <p className={cls.subtitle}>{waitingText}</p>
        </div>

        <div className={cls.playerStrip}>
          {[answerPlayer, guessPlayer].map((player, idx) => (
            <article className={cls.playerMiniCard} key={player?._id || idx}>
              {player?.image?.url ? (
                <img
                  className={cls.playerAvatar}
                  src={player.image.url}
                  alt={player.displayName || "Player"}
                />
              ) : (
                <div className={cls.playerAvatarFallback}>
                  {(player?.displayName || "?").slice(0, 1).toUpperCase()}
                </div>
              )}

              <div className={cls.playerInfo}>
                <p className={cls.infoLabel}>
                  {idx === 0 ? "Answering" : "Guessing"}
                </p>
                <p className={cls.playerName}>{player?.displayName || "-"}</p>
              </div>
            </article>
          ))}
        </div>

        <div className={cls.questionReadOnly}>
          {backgroundUrl ? (
            <div className={cls.backgroundWrap}>
              <div className={cls.backgroundMedia}>
                <Motion.img
                  src={backgroundUrl}
                  alt="Question background"
                  className={cls.questionBackgroundImage}
                  initial={{ scale: 1, y: 0 }}
                  animate={{ scale: 1.05, y: -10 }}
                  transition={{
                    duration: 10,
                    repeat: Infinity,
                    repeatType: "reverse",
                    ease: "easeInOut",
                  }}
                />
              </div>

              <div className={cls.backgroundTopFade} />
              <div className={cls.backgroundBottomFade} />
              <div className={cls.backgroundGlow} />
            </div>
          ) : (
            <div className={cls.backgroundFallback} />
          )}

          <div className={cls.hudFrame}>
            <span className={cls.hudCornerTopLeft} />
            <span className={cls.hudCornerTopRight} />
            <span className={cls.hudCornerBottomLeft} />
            <span className={cls.hudCornerBottomRight} />
          </div>

          <div className={cls.questionOverlay}>
            <span className={cls.questionLabel}>QUESTION</span>

            <h4 className={cls.questionTitle}>
              {question?.title || "Question"}
            </h4>

            {question?.description ? (
              <p className={cls.questionDescription}>{question.description}</p>
            ) : null}

            <div className={cls.answersList}>
              {["A", "B", "C"].map((option) => (
                <div className={cls.answerRow} key={option}>
                  <span className={cls.answerBadge}>{option}</span>
                  <p className={cls.answerText}>
                    {question?.answers?.[option] || "-"}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={cls.stageShell}>
      <div className={cls.glowPink} />
      <div className={cls.glowBlue} />
      <div className={cls.gridLines} />

      <div className={cls.stageHeader}>
        <p className={cls.kicker}>TURN STAGE</p>
        <h3 className={cls.title}>Turn is active</h3>
        <p className={cls.subtitle}>Another player is currently in action.</p>
      </div>
    </section>
  );
}
